import useBrowserStore from "@/store/browserStore";
import useMethodStore from "@/store/MethodStore";
import getCookie from "@/utils/getCookie";
import { ReactNode, useState, useCallback, useReducer, useEffect } from "react";
import { FaX } from "react-icons/fa6";
import { FiHome, FiArrowUpRight } from "react-icons/fi";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { IoRefreshOutline } from "react-icons/io5";

// URL validation function
const sanitizeUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url.startsWith("http") ? url : `https://${url}`);
    return parsedUrl.href;
  } catch {
    return null; // Invalid URL
  }
};

// State type
interface BrowserState {
  urlHistory: string[]; // History of URLs
  currentIndex: number; // Current index in the history
}

// Action type
type BrowserAction =
  | { type: "NAVIGATE"; payload: string } // Navigate to a new URL
  | { type: "GO_BACK" } // Go back in the history
  | { type: "GO_FORWARD" } // Go forward in the history
  | { type: "RESET"; payload: string }; // Reset to the home URL

const initialState: BrowserState = { urlHistory: [""], currentIndex: 0 };

function browserReducer(state: BrowserState, action: BrowserAction): BrowserState {
  switch (action.type) {
    case "NAVIGATE":
      return {
        ...state,
        urlHistory: [...state.urlHistory.slice(0, state.currentIndex + 1), action.payload],
        currentIndex: state.currentIndex + 1,
      };
    case "GO_BACK":
      return { ...state, currentIndex: state.currentIndex - 1 };
    case "GO_FORWARD":
      return { ...state, currentIndex: state.currentIndex + 1 };
    case "RESET":
      return { ...state, urlHistory: [action.payload], currentIndex: 0 };
    default:
      return state;
  }
}

function BrowserWrapper({
  children,
}: {
  children?: ReactNode;
}) {
  const link = useBrowserStore(state => state.link);
  const browserState = useMethodStore(state => state.browserState);

  const userVisitedFirstTime = getCookie("userVisitedFirstTime");

  const [inputValue, setInputValue] = useState<string>(link ?? "");
  const [isIFrame, setIsIFrame] = useState(link ? false : true);
  const [warning, setWarning] = useState(userVisitedFirstTime ? false : true);

  const [state, dispatch] = useReducer(browserReducer, initialState);

  const currentUrl = state.urlHistory[state.currentIndex] || "";

  const submitHandler = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const sanitizedUrl = sanitizeUrl(inputValue.trim());
      if (sanitizedUrl) {
        dispatch({ type: "NAVIGATE", payload: sanitizedUrl });
        setIsIFrame(true);
      } else {
        alert("Invalid URL");
      }
    },
    [inputValue]
  );

  const handleBack = () => {
    if (state.currentIndex > 0) {
      dispatch({ type: "GO_BACK" });
    }
  };

  useEffect(() => {
    if (link) {
      setInputValue(link ?? "")
      dispatch({ type: "NAVIGATE", payload: link });
    }
  }, [link])

  const handleForward = () => {
    if (state.currentIndex < state.urlHistory.length - 1) {
      dispatch({ type: "GO_FORWARD" });
    }
  };

  const handleHome = () => {
    dispatch({ type: "RESET", payload: link ?? "" });
    setInputValue(link ?? "");
    setIsIFrame(false);
  };

  const handleRefresh = () => {
    dispatch({ type: "NAVIGATE", payload: currentUrl });
  };

  return (
    <div
      className={`relative text-zinc-900 dark:text-zinc-100 bg-zinc-100 dark:bg-zinc-950 ${browserState === "maximized" ? "h-full" : "h-full w-full lg:h-[65vh] lg:w-[90vw] lg:max-h-[650px] max-w-[1100px]"
        }`}
    >
      {/* Toolbar */}
      <div className="p-2 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-200 dark:bg-zinc-950 flex justify-center items-center space-x-2">
        {/* Navigation Buttons */}
        <button
          type="button"
          aria-label="Go back"
          onClick={handleBack}
          disabled={state.currentIndex <= 0}
          className={`p-2 border border-zinc-200 dark:border-zinc-800 rounded-md ${state.currentIndex > 0
            ? "text-zinc-900 dark:text-zinc-100 hover:bg-zinc-400/60 dark:hover:bg-zinc-700/80"
            : "text-zinc-400 dark:text-zinc-600 cursor-not-allowed"
            }`}
        >
          <IoMdArrowBack />
        </button>
        <button
          type="button"
          aria-label="Go forward"
          onClick={handleForward}
          disabled={state.currentIndex >= state.urlHistory.length - 1}
          className={`p-2 border border-zinc-200 dark:border-zinc-800 rounded-md ${state.currentIndex < state.urlHistory.length - 1
            ? "text-zinc-900 dark:text-zinc-100 hover:bg-zinc-400/60 dark:hover:bg-zinc-700/80"
            : "text-zinc-400 dark:text-zinc-600 cursor-not-allowed"
            }`}
        >
          <IoMdArrowForward />
        </button>
        <button
          type="button"
          aria-label="Go home"
          onClick={handleHome}
          className="p-2 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-400/60 dark:hover:bg-zinc-700/80 rounded-md"
        >
          <FiHome />
        </button>
        <button
          type="button"
          aria-label="Refresh"
          onClick={handleRefresh}
          className="p-2 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-400/60 dark:hover:bg-zinc-700/80 rounded-md"
        >
          <IoRefreshOutline />
        </button>

        {/* URL Input */}
        <form className="flex-1" onSubmit={submitHandler}>
          <input
            placeholder="Enter URL..."
            className="bg-zinc-100 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 text-sm px-3 py-2 w-full rounded-sm"
            type="url"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <button type="submit" className="hidden">
            Submit
          </button>
        </form>

        {/* Open in New Tab */}
        <a
          href={currentUrl.startsWith("http") ? currentUrl : `https://${currentUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-400/60 dark:hover:bg-zinc-700/80 rounded-md"
          aria-label="Open in new tab"
        >
          <FiArrowUpRight />
        </a>
      </div>

      {/* Content Area */}
      {isIFrame ? (
        <iframe
          src={currentUrl.startsWith("http") ? currentUrl : `https://${currentUrl}`}
          className="w-full h-full"
          title="Browser Content"
          onError={() => alert("Failed to load the URL")}
        ></iframe>
      ) : (
        <div className="w-full h-full">{children}</div>
      )}

      {warning && <p className="absolute text-sm bg-red-500/90 flex justify-between items-center py-0.5 px-3 top-14 left-0 w-full">
        <span>This browser is just an iframe, so some websites may not work properly.</span>
        <button onClick={() => setWarning(false)} className="text-sm">
          <FaX />
        </button>
      </p>}
    </div>
  );
}

export default BrowserWrapper;
